import ProductHero from "@/components/ProductHero";
import ProductDetail from "@/components/ProductDetail";
import ProductCTA from "@/components/ProductCTA";
import SEO from "@/components/SEO";
import SystemCombinations from "@/components/SystemCombinations";
import RelatedSystems from "@/components/RelatedSystems";
import GeoDefinition, { buildDefinedTerm, buildFaqJsonLd } from "@/components/GeoDefinition";
import ProductFaq from "@/components/ProductFaq";
import {
  IntroBlock,
  ProblemBlock,
  ExperienceBlock,
  AudienceBlock,
  BenefitsBlock,
} from "@/components/SectionBlocks";
import { SYSTEMS } from "@/lib/constants";

const s = SYSTEMS.find((x) => x.slug === "ar-ena");

const ARENA_DEFINITION =
  "AR_ENA (Augmented Reality Arena) ist das Action-, Bewegungs- und Competition-System von PINE Systems. Es richtet sich an Betreiber, die ein sportlich-kompetitives Gruppenerlebnis anbieten möchten – mit Bewegung, Teamduellen, Reaktion und Wettbewerb als Kern des Erlebnisses.";

const ARENA_PRODUCT_FAQ = [
  {
    q: "Ist AR_ENA eine kleine PVRA?",
    a: "Nein. AR_ENA ist ein eigenes Systemprofil. Der Fokus liegt auf Bewegung, Competition und Teamduellen, nicht auf parallelen Erlebnisformaten wie bei PVRA.",
  },
  {
    q: "Für welche Zielgruppen ist AR_ENA geeignet?",
    a: "Für Teenager, Erwachsene, sportlich aktive Gruppen, Teams, Firmen-Events und Wettbewerbsformate ab 12 Jahren.",
  },
  {
    q: "Warum steht Bewegung im Mittelpunkt?",
    a: "Weil AR_ENA Gruppen aktiviert: Wer gegeneinander spielt, will Ergebnisse vergleichen, Revanche, Teamwechsel und neue Runden. Bewegung schafft sichtbare Beteiligung und Wiederkehr.",
  },
  {
    q: "Welche Fläche braucht AR_ENA?",
    a: "Orientierungswert ab ca. 200 m² Gesamtfläche bei passenden Standortparametern. Bis zu 8 Spieler auf einem Playfield. Die finale Eignung entsteht aus Raumlogik, Bewegungsfläche und Betreiberstruktur.",
  },
];

const ARENA_QA_FOR_LD = [
  {
    q: "Was unterscheidet AR_ENA von PVRA?",
    a: "AR_ENA ist sportlich-kompetitiv ausgerichtet: Bewegung, Duell, Team, Ergebnis. PVRA ist eine größere Arena mit mehreren parallelen Erlebnisformaten für unterschiedliche Zielgruppen.",
  },
  ...ARENA_PRODUCT_FAQ,
];

export default function ArEna() {
  return (
    <>
      <SEO
        title="AR_ENA – Action, Bewegung und Competition mit System"
        description="AR_ENA ist das Action- und Competition-System von PINE Systems für sportlich-kompetitive Gruppenerlebnisse, Teamduelle und bewegungsorientierte Freizeitflächen."
        ogType="product"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: "AR_ENA — Augmented Reality Arena",
            brand: { "@type": "Brand", name: "PINE Systems" },
            description: ARENA_DEFINITION,
            category: "Location-Based Entertainment System",
          },
          buildDefinedTerm({
            name: "AR_ENA",
            alternateName: "Augmented Reality Arena",
            description: ARENA_DEFINITION,
            url: "https://www.pinexs.de/ar-ena",
          }),
          buildFaqJsonLd(ARENA_QA_FOR_LD),
        ]}
      />
      <ProductHero
        code="02 / SYSTEM"
        name="AR_ENA"
        headline="Action. Bewegung. Competition. Mit System."
        sub="AR_ENA ist das Action-, Competition- und Bewegungsprodukt von PINE Systems für Betreiber, die ein sportlich-kompetitives Gruppenerlebnis anbieten möchten."
        image={s.image}
      />

      <IntroBlock
        overline="Positionierung"
        title="Wenn Bewegung zum Produkt wird."
        text={`AR_ENA richtet sich an Betreiber, die ein aktives, sportlich-kompetitives Erlebnisformat suchen. Der Fokus liegt auf Bewegung, Teamduellen, Reaktion, Wettbewerb und Gruppenenergie.

AR_ENA ist kein ruhiges Technikangebot. AR_ENA ist ein Erlebnis für Standorte, die Dynamik, Aktivität und Competition in den Vordergrund stellen möchten.`}
      />

      <ProblemBlock
        overline="Problem"
        title="Das Problem, das AR_ENA löst."
        text="Viele Action- und Freizeitstandorte suchen nach neuen Formaten, die körperliche Aktivität, digitale Inszenierung und Teamwettbewerb verbinden. Klassische Angebote sind oft entweder sportlich, aber wenig digital, oder digital, aber zu passiv. AR_ENA verbindet Bewegung, Wettbewerb und digitale Erlebnislogik zu einem betreuten Arena-Format."
        cards={[
          {
            title: "Gruppen wollen aktiv werden",
            text: "Viele Gäste wollen nicht nur zuschauen, sondern sich messen, reagieren und gemeinsam gewinnen.",
          },
          {
            title: "Actionflächen brauchen neue Impulse",
            text: "Bestehende Bewegungsflächen können durch digitale Competition-Formate neue Buchungsanlässe bekommen.",
          },
          {
            title: "Wettbewerb schafft Wiederholung",
            text: "Wer gegeneinander spielt, will Ergebnisse vergleichen, Revanche, Teamwechsel und neue Runden.",
          },
        ]}
      />

      <ExperienceBlock
        overline="Erlebnisformate"
        title="Welche Erlebnisse AR_ENA möglich macht."
        text="AR_ENA eignet sich besonders für Gruppen, die Energie, Bewegung und direkten Wettbewerb suchen. Das Erlebnis ist schnell verständlich, körperlich aktiv und emotional stark, weil jeder Teilnehmer sichtbar Teil des Ergebnisses wird."
        items={[
          "Sportlich-kompetitive Teamduelle",
          "Bewegungs- und Reaktionsspiele",
          "Digitale Actionarenen",
          "Competition-Formate für Gruppen",
          "Teambuilding mit Wettbewerbscharakter",
          "Event- und Turnierformate",
          "Teenager- und Erwachsenenformate ab 12 Jahren",
        ]}
      />

      <AudienceBlock
        overline="Zielgruppen"
        title="Für welche Betreiber AR_ENA besonders passt."
        cards={[
          {
            title: "Actionanbieter",
            text: "Für Betreiber, die digitale Action mit echter Bewegung verbinden möchten.",
          },
          {
            title: "Sportliche Freizeitbetriebe",
            text: "Für Standorte, die Competition und Teamduelle in ihr Angebot integrieren wollen.",
          },
          {
            title: "Eventlocations",
            text: "Für Gruppen, Firmen und Events, bei denen Aktivierung und Dynamik gefragt sind.",
          },
          {
            title: "Bestehende Freizeitstandorte",
            text: "Für Betreiber, die ein neues, bewegungsintensives Erlebnisprofil ergänzen möchten.",
          },
        ]}
      />

      <BenefitsBlock
        overline="Nutzen"
        title="Der Nutzen für Ihren Standort."
        items={[
          "Klare Positionierung im Action- und Competition-Segment",
          "Starke Gruppenenergie",
          "Einfache Kommunikation: Bewegung, Duell, Team, Ergebnis",
          "Geeignet für wiederholbare Challenges",
          "Interessant für Teams, Jugendliche, Erwachsene und Events",
          "Auch bei bestimmten asymmetrischen Flächen prüfbar",
        ]}
      />

      <ProductDetail
        forWhom={[
          "Bewegungsorientierte Freizeitflächen",
          "Actionanbieter",
          "Sportliche Erlebnisanbieter",
          "Eventlocations mit Bewegungsfläche",
          "Bestehende Freizeitstandorte",
          "Team- und Competition-Zielgruppen",
        ]}
        strengths={[
          "Bewegung als Erlebnis",
          "Competition statt passiver Unterhaltung",
          "Teamduelle",
          "Hohe Gruppenenergie",
          "Flächenflexible Standortlogik bei passender Prüfung",
        ]}
        orientation={[
          "Bis zu 8 Spieler auf einem Playfield",
          "Ab 200 m² Gesamtfläche bei passenden Standortparametern",
          "Zielgruppe ab 12 Jahren",
        ]}
        ctaLabel="AR_ENA-Standort prüfen lassen"
      />

      <ProductCTA primaryLabel="AR_ENA-Standort prüfen lassen" />

      <RelatedSystems currentSlug="ar-ena" />
      <SystemCombinations />

      <ProductFaq
        productSlug="ar-ena"
        title="Häufige Fragen zu AR_ENA."
        qa={ARENA_PRODUCT_FAQ}
      />

      <GeoDefinition
        term="AR_ENA"
        overline="GEO · Definition"
        definition={ARENA_DEFINITION}
        qa={ARENA_QA_FOR_LD.slice(0, 1)}
        variant="muted"
      />
    </>
  );
}
